import React, { useState, useEffect } from 'react';
import io from 'socket.io-client';
import InteractiveViewer from './InteractiveViewer';
import { API_BASE_URL, SOCKET_URL } from '../config';
import './TeacherInterface.css';

const socket = io(SOCKET_URL);

function TeacherInterface() {
  const [curriculum, setCurriculum] = useState([]);
  const [selectedStandard, setSelectedStandard] = useState(null);
  const [selectedSubject, setSelectedSubject] = useState(null);
  const [selectedChapter, setSelectedChapter] = useState(null);
  const [selectedTopic, setSelectedTopic] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Fetch curriculum
    const apiUrl = API_BASE_URL ? `${API_BASE_URL}/api/curriculum` : '/api/curriculum';
    fetch(apiUrl, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include'
    })
      .then(res => {
        if (!res.ok) {
          throw new Error(`HTTP error! status: ${res.status}`);
        }
        return res.json();
      })
      .then(data => {
        console.log('Curriculum loaded:', data);
        setCurriculum(data);
        setLoading(false);
      })
      .catch(err => {
        console.error('Error fetching curriculum:', err);
        setLoading(false);
        // Show error to user
        alert('Failed to load curriculum. Please check console for details.');
      });

    return () => {
      socket.disconnect();
    };
  }, []);

  const handleStandardSelect = (standard) => {
    setSelectedStandard(standard);
    setSelectedSubject(null);
    setSelectedChapter(null);
    setSelectedTopic(null);
  };

  const handleSubjectSelect = (subject) => {
    setSelectedSubject(subject);
    setSelectedChapter(null);
    setSelectedTopic(null);
  };

  const handleChapterSelect = (chapter) => {
    setSelectedChapter(chapter);
    setSelectedTopic(null);
    socket.emit('select-chapter', chapter.id);
  };

  const handleTopicSelect = (topic) => {
    setSelectedTopic(topic);
    socket.emit('select-topic', topic.id);
  };

  const handleZoom = (level, panX, panY) => {
    socket.emit('zoom', { level, panX, panY });
  };

  const handlePan = (x, y) => {
    socket.emit('pan', { x, y });
  };

  const handleHotspotClick = (hotspot) => {
    socket.emit('select-hotspot', hotspot.id);
  };

  const handleResetView = () => {
    socket.emit('reset-view');
  };

  const handleToggleBloodFlow = (enabled) => {
    socket.emit('toggle-blood-flow', enabled);
    // Also ensure blood flow layer is visible when enabling
    if (enabled) {
      socket.emit('toggle-layer', { layer: 'bloodFlow', visible: true });
    }
  };

  const handleStartCardiacCycle = () => {
    socket.emit('start-cardiac-cycle');
  };

  const handlePauseCardiacCycle = () => {
    socket.emit('pause-cardiac-cycle');
  };

  const handleSelectCardiacPhase = (phaseId) => {
    socket.emit('select-cardiac-phase', phaseId);
  };

  const handleToggleLayer = (layerName, visible) => {
    socket.emit('toggle-layer', { layer: layerName, visible });
  };

  if (loading) {
    return <div className="teacher-interface loading">Loading...</div>;
  }

  const currentSubject = selectedStandard?.subjects?.find(s => s.id === selectedSubject?.id);
  const currentChapter = currentSubject?.chapters?.find(c => c.id === selectedChapter?.id);

  return (
    <div className="teacher-interface">
      <div className="teacher-header">
        <h1>📚 Digital Lab - Teacher Control Panel</h1>
        <p>Welham Boys School</p>
      </div>

      <div className="teacher-content">
        <div className="sidebar">
          {/* Standards */}
          <div className="navigation-section">
            <h2>Standard</h2>
            {loading ? (
              <div className="loading-text">Loading...</div>
            ) : curriculum.length === 0 ? (
              <div className="error-text">No curriculum data available. Please refresh the page.</div>
            ) : (
              <div className="standards-list">
                {curriculum.map(std => (
                  <div
                    key={std.id}
                    className={`nav-item ${selectedStandard?.id === std.id ? 'active' : ''}`}
                    onClick={() => handleStandardSelect(std)}
                  >
                    {std.name}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Subjects */}
          {selectedStandard && (
            <div className="navigation-section">
              <h2>Subject</h2>
              <div className="subjects-list">
                {selectedStandard.subjects.map(subj => (
                  <div
                    key={subj.id}
                    className={`nav-item ${selectedSubject?.id === subj.id ? 'active' : ''}`}
                    onClick={() => handleSubjectSelect(subj)}
                  >
                    {subj.name}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Chapters */}
          {selectedSubject && currentSubject && (
            <div className="navigation-section">
              <h2>Chapters</h2>
              <div className="chapters-list">
                {currentSubject.chapters.map(chapter => (
                  <div
                    key={chapter.id}
                    className={`chapter-item ${selectedChapter?.id === chapter.id ? 'active' : ''}`}
                    onClick={() => handleChapterSelect(chapter)}
                  >
                    <h3>{chapter.title}</h3>
                    <p>{chapter.description}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Topics */}
          {selectedChapter && currentChapter && (
            <div className="navigation-section">
              <h2>Topics</h2>
              <div className="topics-list">
                {currentChapter.topics.map(topic => (
                  <div
                    key={topic.id}
                    className={`topic-item ${selectedTopic?.id === topic.id ? 'active' : ''}`}
                    onClick={() => handleTopicSelect(topic)}
                  >
                    <h4>{topic.title}</h4>
                    <p>{topic.description}</p>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="main-viewer">
          {selectedTopic ? (
            <div className="viewer-container">
              <div className="viewer-header">
                <div className="viewer-title">
                  <h2>{selectedTopic.title}</h2>
                  <div className="breadcrumb">
                    {selectedStandard?.name} → {selectedSubject?.name} → {selectedChapter?.title}
                  </div>
                </div>
                <button onClick={handleResetView} className="reset-btn">
                  Reset View
                </button>
              </div>
              <InteractiveViewer
                topic={selectedTopic}
                onZoom={handleZoom}
                onPan={handlePan}
                onHotspotClick={handleHotspotClick}
                onToggleBloodFlow={handleToggleBloodFlow}
                onStartCardiacCycle={handleStartCardiacCycle}
                onPauseCardiacCycle={handlePauseCardiacCycle}
                onSelectCardiacPhase={handleSelectCardiacPhase}
                onToggleLayer={handleToggleLayer}
                isTeacher={true}
              />
            </div>
          ) : (
            <div className="welcome-screen">
              <h2>Welcome to Digital Lab</h2>
              <p>Select Standard → Subject → Chapter → Topic to begin teaching</p>
              <div className="instructions">
                <h3>How to use:</h3>
                <ul>
                  <li>Start by selecting a Standard (e.g., Class 11)</li>
                  <li>Choose a Subject (e.g., Biology)</li>
                  <li>Select a Chapter from the curriculum</li>
                  <li>Pick a Topic to explore interactively</li>
                  <li>Use zoom, pan, and interactive elements to teach</li>
                  <li>All actions sync to the display panel automatically</li>
                </ul>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default TeacherInterface;
